import { type FormEvent, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { Chat } from '@peacecover/icon';
import CommonHeader from '../components/bar/CommonHeader';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';
import ProfileAvatar from '../my-page/components/ProfileAvatar';
import { useGetBettieFeedPost } from '../../domains/bettie/ui/hooks/useGetBettieFeedPost';
import { useGetBettieFeedComments } from '../../domains/bettie/ui/hooks/useGetBettieFeedComments';
import { useCreateBettieFeedComment } from '../../domains/bettie/ui/hooks/useCreateBettieFeedComment';
import { dateTimeDistanceToNow, formatDateTime } from '../../utils/DateUtil';
import type {
  BettieFeedComment,
  BettieFeedPost
} from '../../domains/bettie/services/bettieFeedService';

const BettieFeedPostDetailPage = () => {
  const { chatRoomId = '', postId = '' } = useParams<{
    chatRoomId: string;
    postId: string;
  }>();
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');

  const { data: post, isLoading: isPostLoading } = useGetBettieFeedPost(chatRoomId, postId);
  const { data: commentsData, isLoading: isCommentsLoading } = useGetBettieFeedComments(
    chatRoomId,
    postId,
    { page: 1, per_page: 50 }
  );
  const createComment = useCreateBettieFeedComment();

  const comments = useMemo(
    () => commentsData?.bettieFeedComments ?? [],
    [commentsData?.bettieFeedComments]
  );

  const handleSubmitComment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const content = comment.trim();
    if (!chatRoomId || !postId || !content || createComment.isLoading) return;

    setCommentError('');
    try {
      await createComment.mutateAsync({
        chatRoomId,
        postId,
        body: {
          bettie_feed_comment: {
            content,
            parent_id: null
          }
        }
      });
      setComment('');
    } catch (_error) {
      setCommentError('댓글을 등록하지 못했어요. 다시 시도해 주세요.');
    }
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark pt-[env(safe-area-inset-top)] pb-[calc(76px+env(safe-area-inset-bottom))]">
      <Helmet>
        <title>{post ? `${getAuthorName(post)} 피드` : '피드'}</title>
      </Helmet>
      <CommonHeader title="피드" />
      <div className="h-[44px]" />
      {isPostLoading ? (
        <div className="pt-[120px] flex justify-center">
          <GradientLoadingSpinner size={32} />
        </div>
      ) : post ? (
        <>
          <FeedPost post={post} />
          <CommentSection comments={comments} isLoading={isCommentsLoading} />
        </>
      ) : null}
      {post && (
        <form
          onSubmit={handleSubmitComment}
          className="fixed bottom-0 left-0 right-0 max-w-prose mx-auto bg-background-light dark:bg-background-dark border-t border-separator1 px-[16px] pt-[10px] pb-[calc(10px+env(safe-area-inset-bottom))]"
        >
          <div className="flex items-center gap-[8px]">
            <input
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="댓글을 입력하세요"
              className="min-w-0 flex-1 rounded-[20px] bg-surface-lv2 px-[16px] py-[10px] text-[14px] leading-[18px] text-text-main-lv1 outline-none placeholder:text-text-main-lv4"
              disabled={createComment.isLoading}
            />
            <button
              type="submit"
              disabled={!comment.trim() || createComment.isLoading}
              className="h-[38px] px-[14px] rounded-[19px] bg-primary-light dark:bg-primary-dark text-[14px] font-semibold leading-none text-white disabled:bg-surface-lv3 disabled:text-text-main-lv4"
            >
              게시
            </button>
          </div>
          {commentError && (
            <p className="mt-[6px] text-[12px] leading-[16px] text-warning dark:text-[#FF495A]">
              {commentError}
            </p>
          )}
        </form>
      )}
    </div>
  );
};

export default BettieFeedPostDetailPage;

const FeedPost = ({ post }: { post: BettieFeedPost }) => {
  const authorName = getAuthorName(post);
  const postedDate = formatFeedDate(post);

  return (
    <article className="flex flex-col">
      <div className="p-[16px] flex items-center gap-[8px]">
        <ProfileAvatar
          thumbnailUrl={post.authorThumbnailUrl}
          widthAndHeight="40px"
          svgWidthAndHeight="40px"
        />
        <div className="flex min-w-0 flex-col items-start gap-[5px]">
          <p className="max-w-full truncate text-[13px] font-semibold leading-[16px] text-text-main-lv2">
            {authorName}
          </p>
          {postedDate && (
            <p className="text-[12px] leading-[14px] text-text-main-lv4">{postedDate}</p>
          )}
        </div>
      </div>
      {post.photoUrl && (
        <img
          src={post.photoUrl}
          alt={post.body || authorName}
          className="w-full aspect-square object-cover bg-surface-lv2"
        />
      )}
      <div className="p-[16px] flex flex-col gap-[12px] items-start border-b border-separator1">
        <div className="flex items-center gap-[3px]">
          <Chat size={24} className="text-text-main-lv2" />
          <span className="text-[13px] font-semibold leading-[16px] text-text-main-lv2">
            {post.commentsCount}
          </span>
        </div>
        {post.body && (
          <p className="text-[13px] font-medium leading-[19px] text-text-main-lv2">
            <span className="font-semibold mr-[8px]">{authorName}</span>
            {post.body}
          </p>
        )}
      </div>
    </article>
  );
};

const CommentSection = ({
  comments,
  isLoading
}: {
  comments: BettieFeedComment[];
  isLoading: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="py-[28px] flex justify-center">
        <GradientLoadingSpinner size={24} />
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="px-[16px] py-[32px]">
        <p className="text-center text-[13px] leading-[18px] text-text-main-lv4">
          첫 댓글을 남겨보세요.
        </p>
      </div>
    );
  }

  return (
    <section className="px-[16px] py-[14px] flex flex-col gap-[18px]">
      {comments.map((comment) => (
        <CommentItem key={comment.bettieFeedCommentId} comment={comment} />
      ))}
    </section>
  );
};

const CommentItem = ({ comment }: { comment: BettieFeedComment }) => {
  return (
    <div className="flex gap-[8px]">
      <ProfileAvatar
        thumbnailUrl={comment.authorThumbnailUrl}
        widthAndHeight="32px"
        svgWidthAndHeight="28px"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-[6px]">
          <p className="truncate text-[13px] font-semibold leading-[16px] text-text-main-lv2">
            {getAuthorName(comment)}
          </p>
          <p className="shrink-0 text-[11px] leading-[14px] text-text-main-lv4">
            {formatRelativeDate(comment.createdAt)}
          </p>
        </div>
        <p className="mt-[4px] text-[13px] leading-[18px] text-text-main-lv2 whitespace-pre-wrap break-words">
          {comment.content}
        </p>
      </div>
    </div>
  );
};

function getAuthorName(author: {
  authorName?: string | null;
  authorId?: string | null;
  authorType?: string | null;
}) {
  return author.authorName || author.authorId || (author.authorType === 'bettie' ? 'Bettie' : '나');
}

function formatFeedDate(post: BettieFeedPost) {
  const date = getPostDate(post);
  if (!date) return '';

  return formatDateTime(date.toISOString(), 'PPP');
}

function formatRelativeDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return dateTimeDistanceToNow(value);
}

function getPostDate(post: BettieFeedPost) {
  const record = post as BettieFeedPost & {
    createdAt?: unknown;
    created_at?: unknown;
    posted_at?: unknown;
    uploadedAt?: unknown;
    uploaded_at?: unknown;
  };

  return (
    parseDate(record.postedAt) ??
    parseDate(record.posted_at) ??
    parseDate(record.uploadedAt) ??
    parseDate(record.uploaded_at) ??
    parseDate(record.createdAt) ??
    parseDate(record.created_at)
  );
}

function parseDate(value: unknown): Date | null {
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  }

  if (!value || typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;
  const candidate =
    record.iso8601 ??
    record.iso ??
    record.value ??
    record.date ??
    record.datetime ??
    record.time ??
    record.timestamp;

  if (typeof candidate !== 'string' && typeof candidate !== 'number') return null;

  const date = new Date(candidate);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}
